/**
 * Dashboard service for the CrewAI RAG application
 * Handles API communication for dashboard data
 */
import api from './api';

export const dashboardService = {
  /**
   * Get summary KPIs for the dashboard header tiles
   * @param {string} startDate - Start date for filtering (YYYY-MM-DD format)
   * @param {string} endDate - End date for filtering (YYYY-MM-DD format)
   * @returns {Promise} Promise object with KPI data
   */
  getKPISummary: async (startDate = null, endDate = null) => {
    try {
      console.log('[Dashboard Service] Fetching KPI summary with date range:', { startDate, endDate });
      
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      // Add 8-second timeout to the request (longer than backend's 5-second timeout)
      const response = await api.get('/dashboard/kpi-summary', { params, timeout: 8000 });
      
      // Enhanced logging for debugging
      console.log('KPI summary response status:', response.status);
      console.log('KPI summary response headers:', response.headers);
      console.log('KPI summary response data:', response.data);
      console.log('Active conversations value:', response.data.activeConversations);
      
      // Verify data structure
      if (response.data && typeof response.data === 'object') {
        // Check if we have the expected properties
        const hasExpectedProps = ['activeConversations', 'activeUsers', 'resolvedQueries', 'pendingResponses']
          .every(prop => prop in response.data);
        
        if (!hasExpectedProps) {
          console.warn('KPI response missing expected properties:', response.data);
        }
      } else {
        console.error('KPI response is not an object:', response.data);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching KPI summary:', error);
      console.error('Error details:', error.response ? error.response.data : 'No response data');
      console.error('Error status:', error.response ? error.response.status : 'No status');
      
      // Return default values on error to prevent UI from breaking
      return {
        activeConversations: 0,
        activeUsers: 0,
        resolvedQueries: 0,
        pendingResponses: 0
      };
    }
  },
  
  /**
   * Get recent activity for the activity feed section
   * @param {number} limit - Maximum number of activities to return
   * @param {string} startDate - Start date for filtering (YYYY-MM-DD format)
   * @param {string} endDate - End date for filtering (YYYY-MM-DD format)
   * @returns {Promise} Promise object with activity data
   */
  getActivityFeed: async (limit = 10, startDate = null, endDate = null) => {
    try {
      console.log('[Dashboard Service] Fetching activity feed with date range:', { limit, startDate, endDate });
      
      const params = { limit };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await api.get('/dashboard/activity-feed', { params });
      
      console.log('Activity feed response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching activity feed:', error);
      throw error;
    }
  },
  
  /**
   * Get daily insights for the dashboard
   * @returns {Promise} Promise object with insights data
   */
  getDailyInsights: async () => {
    try {
      console.log('Fetching daily insights');
      
      const response = await api.get('/dashboard/daily-insights');
      
      console.log('Daily insights response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching daily insights:', error);
      throw error;
    }
  },
  
  /**
   * Get AI-generated insights and alerts
   * @param {string} startDate - Start date for the date range (optional)
   * @param {string} endDate - End date for the date range (optional)
   * @returns {Promise} Promise object with AI insights
   */
  getAIInsights: async (startDate = null, endDate = null) => {
    try {
      console.log('[Dashboard Service] Fetching AI insights with date range:', { startDate, endDate });
      
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await api.get('/dashboard/ai-insights', { params });
      
      console.log('[Dashboard Service] AI insights response:', response.data);
      return response.data;
    } catch (error) {
      console.error('[Dashboard Service] Error fetching AI insights:', error);
      throw error;
    }
  },

  /**
   * Manually trigger dashboard metrics calculation
   * @param {string} startDate - Start date for the date range (optional)
   * @param {string} endDate - End date for the date range (optional)
   * @returns {Promise} Promise object with calculation result
   */
  calculateMetrics: async () => {
    try {
      console.log('Triggering metrics calculation');
      
      const response = await api.post('/dashboard/calculate-metrics');
      
      console.log('Metrics calculation response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error calculating metrics:', error);
      throw error;
    }
  },

  /**
   * Get dashboard metrics refresh status
   * @returns {Promise} Promise object with last refresh time and status
   */
  getRefreshStatus: async () => {
    try {
      console.log('Fetching refresh status');
      
      const response = await api.get('/dashboard/refresh-status');
      
      console.log('Refresh status response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching refresh status:', error);
      throw error;
    }
  },

  // ===== COMPREHENSIVE ANALYTICS FUNCTIONS =====

  /**
   * Get comprehensive widget analytics
   * @returns {Promise} Promise object with widget analytics data
   */
  getWidgetAnalytics: async () => {
    try {
      console.log('Fetching widget analytics');
      
      const response = await api.get('/dashboard/widget-analytics');
      
      console.log('Widget analytics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching widget analytics:', error);
      throw error;
    }
  },

  /**
   * Get comprehensive performance metrics
   * @returns {Promise} Promise object with performance metrics
   */
  getPerformanceMetrics: async () => {
    try {
      console.log('Fetching performance metrics');
      
      const response = await api.get('/dashboard/performance-metrics');
      
      console.log('Performance metrics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
      throw error;
    }
  },

  /**
   * Get comprehensive quality metrics
   * @returns {Promise} Promise object with quality metrics
   */
  getQualityMetrics: async () => {
    try {
      console.log('Fetching quality metrics');
      
      const response = await api.get('/dashboard/quality-metrics');
      
      console.log('Quality metrics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching quality metrics:', error);
      throw error;
    }
  },

  /**
   * Get comprehensive user analytics
   * @returns {Promise} Promise object with user analytics
   */
  getUserAnalytics: async () => {
    try {
      console.log('Fetching user analytics');
      
      const response = await api.get('/dashboard/user-analytics');
      
      console.log('User analytics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching user analytics:', error);
      throw error;
    }
  },

  /**
   * Get knowledge base insights
   * @returns {Promise} Promise object with knowledge base insights
   */
  getKnowledgeBaseInsights: async () => {
    try {
      console.log('Fetching knowledge base insights');
      
      const response = await api.get('/dashboard/knowledge-base-insights');
      
      console.log('Knowledge base insights response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching knowledge base insights:', error);
      throw error;
    }
  },

  /**
   * Get all comprehensive analytics in one call (optimized)
   * @returns {Promise} Promise object with all analytics data
   */
  getComprehensiveAnalytics: async () => {
    try {
      console.log('[Dashboard Service] Fetching comprehensive analytics');
      
      const response = await api.get('/dashboard/comprehensive-analytics');
      
      console.log('Comprehensive analytics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching comprehensive analytics:', error);
      throw error;
    }
  },

  /**
   * Get conversations that require manual review (pending status)
   * @param {number} limit - Maximum number of pending conversations to return
   * @returns {Promise} Promise object with pending review data
   */
  getPendingReviewConversations: async (limit = 10) => {
    try {
      console.log('[Dashboard Service] Fetching pending review conversations with limit:', limit);
      
      const response = await api.get(`/dashboard/pending-review?limit=${limit}`);
      
      console.log('Pending review conversations response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching pending review conversations:', error);
      throw error;
    }
  },

  /**
   * Get daily time-series (conversations/day, enquiries/day) for Analytics charts.
   * @param {number} days - window size (1..90)
   */
  getTimeseries: async (days = 30) => {
    const response = await api.get(`/dashboard/timeseries?days=${days}`);
    return response.data;
  }
};

export default dashboardService;
